import { useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { activeNetworkSelector } from '../../../redux/selectors';
import { useGetMovementsQuery } from '../../../services/movements';
import { useMovementsColumns, getMovementsHeader } from './useMovementsColumns';
import { useMovementsFilters } from './useMovementsFilters';
import { isDefaultFilters } from '../defaultFilters';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

export const useMovementsPage = () => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const defaultColumnsBase = useMovementsColumns();
  const tableRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);

  const { filters, setFilters, filtersCount, queryParams: urlQueryParams, setQueryParams } = useMovementsFilters();

  const page = urlQueryParams.page || 1;

  const [columnsBase, setColumnsBase] = useLocalStorage(
    `${activeNetwork?.id}-movements-columns-order-v1`,
    defaultColumnsBase,
  );

  const apiQueryParams = useMemo(() => {
    const movementType = (() => {
      if (!Array.isArray(filters.movementType) || filters.movementType.length === 0) return undefined;
      const values = filters.movementType.map((item) =>
        typeof item === 'object' && item?.value !== undefined ? item.value : item,
      );
      return values.join('|');
    })();

    const companyIds = (() => {
      if (!Array.isArray(filters.companies) || filters.companies.length === 0) return undefined;
      const ids = filters.companies.map((item) =>
        typeof item === 'object' && item?.value !== undefined ? item.value : item,
      );
      return ids.join('|');
    })();

    const sharedListIds = (() => {
      if (!Array.isArray(filters.sharedListIds) || filters.sharedListIds.length === 0) return undefined;
      const ids = filters.sharedListIds.map((item) =>
        typeof item === 'object' && item?.value !== undefined ? item.value : item,
      );
      return ids.join('|');
    })();

    return {
      networkId: activeNetwork?.id,
      page,
      perPage: 20,
      sortBy: filters.sortBy || 'date',
      sortDirection: filters.sortDirection || 'desc',
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      movementType,
      companyIds,
      sharedListIds,
      contactScope: filters.contactScope || undefined,
    };
  }, [filters, page, activeNetwork?.id]);

  const { data, isLoading, isFetching } = useGetMovementsQuery(apiQueryParams);

  const movementsCount = data?.meta?.totalCount ?? 0;

  const columns = useMemo(() => {
    const flattenedColumns = defaultColumnsBase.reduce((acc, column) => {
      acc[column.accessor] = column;
      return acc;
    }, {});

    return columnsBase.map((column) => {
      let header = column.accessor === 'contact' ? getMovementsHeader(movementsCount) : column.Header;
      if ((isLoading || (isFetching && page === 1)) && column.accessor === 'contact') {
        header = 'Loading...';
      }

      return {
        ...flattenedColumns[column.accessor],
        pinned: column.pinned,
        Header: header,
        show: column.show,
        width: column.width,
      };
    });
  }, [columnsBase, defaultColumnsBase, movementsCount, isLoading, isFetching, page]);

  const setColumns = useCallback(
    (newColumns) => {
      setColumnsBase(newColumns);
    },
    [setColumnsBase],
  );

  const showMainSkeleton = isLoading && page === 1;
  const showRenderSkeleton = isFetching && page === 1 && !isLoading;

  const hasActiveFilters = useMemo(() => !isDefaultFilters(filters), [filters]);

  const hasMore = useMemo(() => {
    if (!data?.items || !data?.meta) return false;
    const totalCount = data.meta.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / apiQueryParams.perPage);
    return page < totalPages;
  }, [data, apiQueryParams, page]);

  const onLoadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setQueryParams({
        ...urlQueryParams,
        page: page + 1,
      });
    }
  }, [hasMore, isFetching, setQueryParams, urlQueryParams, page]);

  const onSort = useCallback(
    ({ sortBy: newSortBy, sortDirection: newSortDirection }) => {
      setFilters({
        sortBy: newSortBy,
        sortDirection: newSortDirection,
      });
    },
    [setFilters],
  );

  const initialSortBy = useMemo(
    () => ({
      id: 'date',
      desc: (filters.sortDirection || 'desc') === 'desc',
    }),
    [filters.sortDirection],
  );

  const currentSort = useMemo(
    () => ({
      sortBy: filters.sortBy || 'date',
      sortDirection: filters.sortDirection || 'desc',
    }),
    [filters.sortBy, filters.sortDirection],
  );

  const movementsSortOptions = useMemo(() => [{ field: 'date', label: 'Date', value: 'date' }], []);

  const toolbarOptionChange = useCallback(
    (tool, ...args) => {
      switch (tool) {
        case 'filter':
          setShowFilters(!showFilters);
          break;
        case 'sort': {
          const [{ value, direction }] = args;
          setFilters({
            sortBy: value,
            sortDirection: direction,
          });
          break;
        }
        case 'columns':
          setColumns(...args);
          break;
        default:
          break;
      }
    },
    [showFilters, setFilters, setColumns],
  );

  return {
    data,
    isLoading,
    isFetching,
    filters,
    setFilters,
    filtersCount,
    urlQueryParams,
    showFilters,
    setShowFilters,
    columns,
    setColumns,
    showMainSkeleton,
    showRenderSkeleton,
    hasActiveFilters,
    hasMore,
    onLoadMore,
    isLoadingMore: isFetching && page > 1,
    tableRef,
    initialSortBy,
    currentSort,
    movementsSortOptions,
    toolbarOptionChange,
    onSort,
    activeNetwork,
    defaultColumns: defaultColumnsBase,
    page,
  };
};
